import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import {
  SpecialTextField,
  SectionHeader,
  TableAlerts,
  Tooltip,
} from "../../../../../app/components";
import { useStudyPlan } from "../../../../../app/contexts/study.context";
import SeparateContainer from "../../../../../app/components/separate.Container";
import SideBySide from "../../../../../app/components/sideBySide";
import { usePauseValidation } from "../../../../../app/hooks/useStudyPlanValidations";
import { ActivatedSitesStats } from "../../../activated.sites.stats";
import { useActivatedSitesLiveTotal } from "../../../../../app/hooks/useActivatedSitesLiveTotal";
import { getSitesAndSubjectsParameters, isEmpty } from "../../../utils/helpers";
import { formatInputValue } from "../../../../../app/helpers/utils";
import { STUDY_PLAN_TYPES } from "../../../../../app/contexts/constants/study.context.constants";
import { toParticipantUiText } from "../../../../../app/utils/participantTerminology";

const ScenarioB = ({ values, setValues, setFieldErrors }) => {
  const { studyPlan, updatePlan, isSequential, siteActivationStats } = useStudyPlan();

  const {
    screenFailRate,
    dropOutRate,
    screeningPeriodDuration,
    overallEnrollmentDuration,
    treatmentDuration,
    followUpDuration,
    parameters,
    milestones,
    pauseEnrollmentAfterPatient = null,
    pauseEnrollmentDays = null,
    cohortType,
    studyPlanTypeDetail,
  } = studyPlan;
  const [sites_to_activate, subjects_to_screen] = getSitesAndSubjectsParameters(studyPlan?.parameters);
  const [
    number_patients_enrolled,
    number_patients_treatment,
    number_patients_follow_up,
  ] = milestones.filter(
    ({ milestoneId }) =>
      milestoneId === 144 || milestoneId === 147 || milestoneId === 150,
  );

  const isNotSequentialCohort = useMemo(
    () => cohortType !== 2 && studyPlanTypeDetail !== "Cohort (D)",
    [cohortType, studyPlanTypeDetail],
  );

  const [PauseEnrollment, setPauseEnrollment] = useState({
    pauseEnrollmentAfterPatient: pauseEnrollmentAfterPatient,
    pauseEnrollmentDays: pauseEnrollmentDays,
  });

  const [afterShowEmptyError, setAfterShowEmptyError] = useState(false);
  const [daysShowEmptyError, setDaysShowEmptyError] = useState(false);

  const isRequired = (lowerBound, rate) => isEmpty(lowerBound) && isEmpty(rate);

  const isNumberPatientsEnrolledOrScreenFaiLRateRequired = isRequired(
    number_patients_enrolled.lowerBound,
    screenFailRate,
  );

  const isPatientsCompletingTreatmentOrDropOutRateRequired = isRequired(
    number_patients_treatment.lowerBound,
    dropOutRate,
  );

  const handlePauseEnrollmentChange = (field, value) => {
    const formattedValue = formatInputValue(value);

    setPauseEnrollment({
      ...PauseEnrollment,
      [field]: formattedValue,
    });

    if (field === "pauseEnrollmentDays") {
      updatePlan("pauseEnrollmentDays", formattedValue);
    }

    if (field === "pauseEnrollmentAfterPatient") {
      updatePlan("pauseEnrollmentAfterPatient", formattedValue);
    }

    setValues({
      ...values,
      ...studyPlan,
      [field]: formattedValue,
      updated: new Date().getTime(),
    });
  };

  const customValidationDefinition = useMemo(
    () => ({
      "overallenrollmentduration(days)": {
        range: [1, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      numberofactivatedsites: {
        range: [1, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      numberofpatientsscreened: {
        range: [1, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      "screeningperiodperpatient(days)": {
        range: [0, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      "treatmentperiodperpatient(days)": {
        range: [0, Infinity],
        validateEmpty: true,
        checkIfInt: true,
        checkIfDec: false,
      },
      "follow-upperiodperpatient(days)": {
        range: [0, Infinity],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      "screenfailrate(indecimals)": {
        range: [0.0, 0.99],
        validateEmpty: false,
        checkIfInt: false,
        checkIfDec: true,
      },
      "dropoutrate(indecimals)": {
        range: [0.0, 0.99],
        validateEmpty: false,
        checkIfInt: false,
        checkIfDec: true,
      },
      numberofpatientsenrolled: {
        range: [1, subjects_to_screen.lowerBound],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      numbrofpatientscompletingtreatment: {
        range: [
          1,
          number_patients_enrolled.lowerBound || subjects_to_screen.lowerBound,
        ],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      numbrofpatientscompletingfollowup: {
        range: [
          1,
          number_patients_treatment.lowerBound ||
            number_patients_enrolled.lowerBound ||
            subjects_to_screen.lowerBound,
        ],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
      pauseEnrollment: {
        range: [1, 9999],
        validateEmpty: false,
        checkIfInt: true,
        checkIfDec: false,
      },
    }),
    // eslint-disable-next-line
    [
      subjects_to_screen.lowerBound,
      number_patients_enrolled.lowerBound,
      number_patients_treatment.lowerBound,
      screenFailRate,
    ],
  );

  const handleFieldChange = (type, field, key, value) => {
    const formattedValue = formatInputValue(value);
    const isParameter = type === "parameters";
    const array = isParameter ? parameters : milestones;
    const keyName = isParameter ? "parameterName" : "milestoneName";
    
    const index = array.map((item) => item[keyName]).indexOf(key);
    const newArray = [...array];
    newArray[index][field] = formattedValue;

    updatePlan(type, newArray);
    setValues({ 
      ...values, 
      [type]: newArray,
      updated: new Date().getTime() 
    });
  };

  useEffect(() => {
    const { pauseEnrollmentDays, pauseEnrollmentAfterPatient } =
      PauseEnrollment;

    const isDaysEmpty = !pauseEnrollmentDays;
    const isAfterPatientEmpty = !pauseEnrollmentAfterPatient;

    const pauseEnrollmentAfterShouldShowEmptyError =
      pauseEnrollmentAfterPatient &&
      (pauseEnrollmentDays === "" || isDaysEmpty);

    const pauseEnrollmentDaysShouldDisablePauseEnrollment =
      pauseEnrollmentDays &&
      (pauseEnrollmentAfterPatient === "" || isAfterPatientEmpty);

    setAfterShowEmptyError(pauseEnrollmentDaysShouldDisablePauseEnrollment);
    setDaysShowEmptyError(pauseEnrollmentAfterShouldShowEmptyError);

    if (isDaysEmpty && isAfterPatientEmpty) {
      setFieldErrors((prevState) => {
        const { undefined: _, pauseEnrollmentError, ...rest } = prevState;
        return rest;
      });
    } else if (isDaysEmpty || isAfterPatientEmpty) {
      setFieldErrors((prevState) => ({
        ...prevState,
        pauseEnrollmentError: {
          ...prevState.pauseEnrollmentError,
          errors: ["Can't be empty."],
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { undefined: _, pauseEnrollmentError, ...rest } = prevState;
        return rest;
      });
    }
  }, [
    PauseEnrollment.pauseEnrollmentDays,
    PauseEnrollment.pauseEnrollmentAfterPatient,
    setFieldErrors,
    PauseEnrollment,
  ]);

  useEffect(() => {
    if (isNumberPatientsEnrolledOrScreenFaiLRateRequired) {
      setFieldErrors((prevState) => ({
        ...prevState,
        requiredError: {
          ...prevState.requiredError,
          errors: ["Can't be empty."],
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { requiredError, ...rest } = prevState;
        return rest;
      });
    }
    // eslint-disable-next-line
  }, [screenFailRate, number_patients_enrolled.lowerBound]);

  useEffect(() => {
    if (isPatientsCompletingTreatmentOrDropOutRateRequired) {
      setFieldErrors((prevState) => ({
        ...prevState,
        dropTreatmentRequired: {
          ...prevState.dropTreatmentRequired,
          errors: ["Can't be empty."],
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { dropTreatmentRequired, ...rest } = prevState;
        return rest;
      });
    }
    // eslint-disable-next-line
  }, [dropOutRate, number_patients_treatment.lowerBound]);
  
  const pauseAlerts = usePauseValidation(
    number_patients_enrolled.lowerBound,
    PauseEnrollment,
    screenFailRate,
    subjects_to_screen.lowerBound,
  );

  const liveAllCountriesSites = useActivatedSitesLiveTotal(
    studyPlan.studyPlanType,
    Number(sites_to_activate?.lowerBound) || 0,
    Number(sites_to_activate?.lowerBound) || 0,
    0,
    0,
    0,
    0,
    Number(siteActivationStats?.sitesActivated) || 0,
  );
  
  return (
    <Container>
      <TwoColumnLayout>
        <FlexCol>
          <SeparateContainer>
            <SideBySide>
              <SectionHeader
                hasStartAdornment={false}
                marginBottom={false}
                paddingBottom={false}
              >
                Overall Parameters
              </SectionHeader>
            </SideBySide>
            <div style={{ padding: "20px" }}>
              {!isSequential && (
                <div style={{ padding: "0.5em" }}>
                  <SpecialTextField
                    label="Overall Enrollment Duration (days)"
                    value={overallEnrollmentDuration}
                    onChange={(value) => {
                      updatePlan("overallEnrollmentDuration", value);
                    }}
                    required
                    showArrows={false}
                    customValidation={
                      customValidationDefinition[
                        "overallenrollmentduration(days)"
                      ]
                    }
                    setFieldErrors={setFieldErrors}
                  />
                </div>
              )}
              <div style={{ padding: "0.5em" }}>
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  label={sites_to_activate.parameterDescr}
                  value={sites_to_activate.lowerBound}
                  onChange={(value) => {
                    handleFieldChange(
                      "parameters",
                      "lowerBound",
                      sites_to_activate.parameterName,
                      value,
                    );
                  }}
                  key={sites_to_activate.parameterName}
                  required
                  showArrows={false}
                  customValidation={
                    customValidationDefinition["numberofactivatedsites"]
                  }>
                    <ActivatedSitesStats overrides={liveAllCountriesSites} 
                      studyPlanType={studyPlan.studyPlanType} />
                  </SpecialTextField>
              </div>
            </div>
          </SeparateContainer>
          <SeparateContainer>
            <div>
              <SideBySide>
                <SectionHeader
                  hasStartAdornment={false}
                  marginBottom={false}
                  paddingBottom={false}
                >
                  Per Participant Duration from SOE
                </SectionHeader>
              </SideBySide>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ paddingInline: "0.5em" }}>
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  label="Screening Period Per Participant (days)"
                  value={screeningPeriodDuration}
                  onChange={(value) =>
                    updatePlan("screeningPeriodDuration", value)
                  }
                  required
                  showArrows={false}
                  customValidation={
                    customValidationDefinition[
                      "screeningperiodperpatient(days)"
                    ]
                  }
                />
              </div>
              <div style={{ padding: "0.5em" }}>
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  label="Treatment Period Per Participant (days)"
                  value={treatmentDuration}
                  onChange={(value) => updatePlan("treatmentDuration", value)}
                  required
                  showArrows={false}
                  customValidation={
                    customValidationDefinition[
                      "treatmentperiodperpatient(days)"
                    ]
                  }
                />
              </div>
              <div style={{ padding: "0.5em" }}>
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  label="Follow-Up Period Per Participant (days)"
                  value={followUpDuration}
                  onChange={(value) => updatePlan("followUpDuration", value)}
                  endadornment={
                    <Tooltip info="If applicable to protocol. Make sure to also complete the Follow-Up Period Per Participant (days) field." />
                  }
                  showArrows={false}
                  customValidation={
                    customValidationDefinition[
                      "follow-upperiodperpatient(days)"
                    ]
                  }
                />
              </div>
            </div>
          </SeparateContainer>
          {isNotSequentialCohort && (
            <SeparateContainer>
              <div>
                <SideBySide>
                  <SectionHeader
                    hasStartAdornment={false}
                    marginBottom={false}
                    paddingBottom={false}
                    endAdornment={
                      <Tooltip info="Complete only if a pause in enrollment is expected. For example, if an Interim Analysis is needed after the first 100 participants completes three months of treatment before proceeding, include 100 in the first field and then calculate when screening would resume in the second (consider the treatment time for the 100th participant plus time for the associated data entry and completing and reviewing the interim analysis). Reminder: the second field is when participant screening (NOT enrollment) resumes." />
                    }
                  >
                    Screening/Enrollment Pause (if applicable)
                  </SectionHeader>
                </SideBySide>
              </div>
              <TableAlerts alerts={pauseAlerts} />
              <div style={{ padding: "20px" }}>
                <div style={{ padding: "0.5em" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Pause Enrollment After Participant #"
                    value={PauseEnrollment.pauseEnrollmentAfterPatient}
                    showCustomError={afterShowEmptyError}
                    customMessageError={"Can't be empty."}
                    required={!isEmpty(PauseEnrollment.pauseEnrollmentDays)}
                    showTooltip={
                      PauseEnrollment.pauseEnrollmentDays ? true : false
                    }
                    showArrows={false}
                    customValidation={
                      customValidationDefinition["pauseEnrollment"]
                    }
                    onChange={(value) => {
                      handlePauseEnrollmentChange(
                        "pauseEnrollmentAfterPatient",
                        value,
                      );
                    }}
                  />
                </div>
                <div style={{ padding: "0.5em" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Resume Screening After (days)"
                    showCustomError={daysShowEmptyError}
                    customMessageError={"Can't be empty."}
                    value={PauseEnrollment.pauseEnrollmentDays}
                    showTooltip={
                      PauseEnrollment.pauseEnrollmentAfterPatient ? true : false
                    }
                    showArrows={false}
                    required={!isEmpty(PauseEnrollment.pauseEnrollmentAfterPatient)}
                    customValidation={
                      customValidationDefinition["pauseEnrollment"]
                    }
                    onChange={(value) => {
                      handlePauseEnrollmentChange("pauseEnrollmentDays", value);
                    }}
                  />
                </div>
              </div>
            </SeparateContainer>
          )}
        </FlexCol>
        <FlexCol>
          <SeparateContainer>
            <SideBySide>
              <SectionHeader
                hasStartAdornment={false}
                marginBottom={false}
                paddingBottom={false}
              >
                Participant Numbers
              </SectionHeader>
            </SideBySide>
            <div style={{ padding: "20px" }}>
              <div style={{ padding: "0.5em" }}>
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  label={toParticipantUiText(subjects_to_screen.parameterDescr)}
                  value={subjects_to_screen.lowerBound}
                  onChange={(value) => {
                    handleFieldChange(
                      "parameters",
                      "lowerBound",
                      subjects_to_screen.parameterName,
                      value,
                    );
                    setValues({ ...values, ...studyPlan, updated: new Date().getTime() });
                  }}
                  key={subjects_to_screen.parameterName}
                  required
                  showArrows={false}
                  customValidation={
                    customValidationDefinition["numberofpatientsscreened"]
                  }
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  background: "var(--surface-50)",
                  padding: "0.5em",
                  border: "1px solid var(--surface-50)",
                  borderRadius: "4px",
                  marginBlock: "0.5em",
                }}
              >
                <div style={{ flex: 1 }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Number of Participants Enrolled"
                    value={number_patients_enrolled.lowerBound}
                    onChange={(value) => handleFieldChange(
                      "milestones",
                      "lowerBound",
                      number_patients_enrolled.milestoneName,
                      value,
                    )}
                    disabled={!isEmpty(screenFailRate)}
                    required={isEmpty(screenFailRate)}
                    showCustomError={
                      isNumberPatientsEnrolledOrScreenFaiLRateRequired
                    }
                    customMessageError={"Can't be empty."}
                    key={number_patients_enrolled.milestoneName}
                    showArrows={false}
                    customValidation={
                      customValidationDefinition["numberofpatientsenrolled"]
                    }
                  />
                </div>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Screen Fail Rate (in decimals)"
                    value={screenFailRate}
                    disabled={!isEmpty(number_patients_enrolled.lowerBound)}
                    required={isEmpty(number_patients_enrolled.lowerBound)}
                    onChange={(value) => {
                      const formattedValue = formatInputValue(value);
                      updatePlan("screenFailRate", formattedValue)
                      setValues({
                        ...values,
                        ...studyPlan,
                        screenFailRate: formattedValue,
                        updated: new Date().getTime(),
                      });
                    }}
                    inputProps={{ step: 0.01 }}
                    min={0.01}
                    showCustomError={
                      isNumberPatientsEnrolledOrScreenFaiLRateRequired
                    }
                    customMessageError={"Can't be empty."}
                    showArrows={false}
                    customValidation={
                      customValidationDefinition["screenfailrate(indecimals)"]
                    }
                    endadornment={
                      <Tooltip
                        zIndex={10000}
                        info="Either the Screen Fail Rate OR the Number of Participants Enrolled must be completed here."
                      />
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1em",
                  background: "var(--surface-50)",
                  padding: "0.5em",
                  border: "1px solid var(--surface-50)",
                  borderRadius: "4px",
                  marginBlock: "0.5em",
                }}
              >
                <div style={{ flex: 1 }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    key={number_patients_treatment.milestoneId}
                    label={toParticipantUiText(number_patients_treatment.milestoneLabel)}
                    value={number_patients_treatment.lowerBound}
                    onChange={(value) => handleFieldChange(
                      "milestones",
                      "lowerBound",
                      number_patients_treatment.milestoneName,
                      value,
                    )}
                    disabled={!isEmpty(dropOutRate)}
                    required={isEmpty(dropOutRate)}
                    showArrows={false}
                    customMessageError={"Can't be empty."}
                    showCustomError={
                      isPatientsCompletingTreatmentOrDropOutRateRequired
                    }
                    customValidation={
                      customValidationDefinition[
                        "numbrofpatientscompletingtreatment"
                      ]
                    }
                  />
                </div>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <SpecialTextField
                    setFieldErrors={setFieldErrors}
                    label="Drop Out Rate (in decimals)"
                    value={dropOutRate}
                    disabled={!isEmpty(number_patients_treatment.lowerBound)}
                    required={isEmpty(number_patients_treatment.lowerBound)}
                    onChange={(value) => updatePlan("dropOutRate", value)}
                    inputProps={{ step: 0.01 }}
                    min={0.01}
                    showArrows={false}
                    showCustomError={
                      isPatientsCompletingTreatmentOrDropOutRateRequired
                    }
                    customMessageError={"Can't be empty."}
                    customValidation={
                      customValidationDefinition["dropoutrate(indecimals)"]
                    }
                    endadornment={
                      <Tooltip info="Either the Drop Out Rate OR the Number of Participants Completing Treatment must be completed here." />
                    }
                  />
                </div>
              </div>
              <div style={{ padding: "0.5em" }}>
                <SpecialTextField
                  setFieldErrors={setFieldErrors}
                  key={number_patients_follow_up.milestoneId}
                  label={toParticipantUiText(number_patients_follow_up.milestoneLabel)}
                  value={number_patients_follow_up.lowerBound}
                  onChange={(value) => handleFieldChange(
                    "milestones",
                    "lowerBound",
                    number_patients_follow_up.milestoneName,
                    value,
                  )}
                  endadornment={
                    <Tooltip
                      info={
                        MILESTONE_INFO[number_patients_follow_up.milestoneName]
                      }
                    />
                  }
                  showArrows={false}
                  customValidation={
                    customValidationDefinition[
                      "numbrofpatientscompletingfollowup"
                    ]
                  }
                />
              </div>
            </div>
          </SeparateContainer>
        </FlexCol>
      </TwoColumnLayout>
    </Container>
  );
};

export default ScenarioB;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0.5em;
`;
const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 0.5em;
  margin-top: 1em;
`;
const MILESTONE_INFO = {
  NUM_SUBJECTS_ENROLLED:
    "If a certain number of enrolled participants is required in a specific country, also complete this at the country level. Either the Screen Fail Rate OR the Number of Participants Enrolled must be completed here or at the country level.",
  NUM_SUBJECTS_COMPLETED:
    "If applicable to protocol. Make sure to also complete the Number of Participants Completing Follow-up field.",
};

const FlexCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;
